import React, { useEffect, useMemo, useState } from 'react';
import { ScrollView, View, Text, Dimensions, TouchableOpacity, ActivityIndicator } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesome5 } from '@expo/vector-icons';
import { Map, AdvancedMarker, Pin, InfoWindow } from '@vis.gl/react-google-maps';

import { getReservationsByTrackingId, getTrackersData } from '../../api/TrackingTags';
import { msgStr } from '../../common/constants/Message';
import { useAlertModal } from '../../common/hooks';
import BasicLayout from '../../common/components/CustomLayout/BasicLayout';
import { MapPosition } from '../../common/constants/AppConstants';
import { GOOGLE_MAP_Id } from '../../../env';
import { fetchProfileSettings } from '../../redux/slices/profileSettingsSlice';
import { AppDispatch, RootState } from '../../redux/store';

import { trackingTagsStyle } from './styles/TrackingTagsStyle';
import InfoWindowCard from '../dashboard/InfoWindowCard';

const TrackingTags = ({ navigation, route }) => {
  const screenHeight = Dimensions.get('window').height;
  const dispatch = useDispatch<AppDispatch>();
  const { settings } = useSelector((state: RootState) => state.profileSettings);

  const { showAlert } = useAlertModal();

  const [trackers, setTrackers] = useState([]);
  const [selectedTagId, setSelectedTagId] = useState(route?.params?.tag ?? null);
  const [selectedTracker, setSelectedTracker] = useState<any>(null);
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const [infoWindowDetail, setInfoWindowDetail] = useState<{ lat: number; lng: number; tracker: any } | null>(null);
  const [reservations, setReservations] = useState<any[]>([]);
  const [reservationsLoading, setReservationsLoading] = useState(false);
  const [trackingTagsEnabled, setTrackingTagsEnabled] = useState(true);

  const headerStats = useMemo(() => {
    const status = selectedTracker?.status || {};
    const speed = status?.speed;
    const signal = status?.signal;
    const lastSeen = status?.status_last_updated ? new Date(status.status_last_updated).toLocaleString() : null;
    const powerConnected = status?.charge ? 'Power Connected' : 'On Battery';
    return { speed, signal, lastSeen, powerConnected };
  }, [selectedTracker]);

  // Load tracking toggle setting
  useEffect(() => {
    dispatch(fetchProfileSettings(undefined));
  }, [dispatch]);

  useEffect(() => {
    const trackingSetting = settings?.find(
      (s) => s.item_key === 'tracking_tags_enabled'
    );
    if (trackingSetting) {
      const normalized = `${trackingSetting.item_value}`.toLowerCase();
      setTrackingTagsEnabled(normalized === 'true');
    }
  }, [settings]);

  useEffect(() => {
    let isActive = true;

    if (!trackingTagsEnabled) {
      setTrackers([]);
      setSelectedTracker(null);
      setSelectedTagId(null);
      setShowInfoWindow(false);
      setInfoWindowDetail(null);
      return () => {
        isActive = false;
      };
    }

    const fetchTrackers = () => {
      getTrackersData((jsonRes, status) => {
        if (!isActive) return;
        switch (status) {
          case 200:
            setTrackers(jsonRes || []);
            break;
          case 500:
            showAlert('error', msgStr('serverError'));
            break;
          default:
            if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
            else showAlert('error', msgStr('unknownError'));
            break;
        }
      });
    };

    fetchTrackers();
    const intervalId = setInterval(fetchTrackers, 30000);

    return () => {
      isActive = false;
      clearInterval(intervalId);
    };
  }, [trackingTagsEnabled]);

  useEffect(() => {
    if (!trackers || trackers.length === 0) {
      setSelectedTracker(null);
      return;
    }

    const incoming = route?.params?.tag;
    const targetId = incoming || selectedTagId || trackers[0]?._id || null;
    const found = trackers.find((t) => t?._id === targetId) || trackers[0];

    setSelectedTagId(found?._id || null);
    setSelectedTracker(found || null);
    if (found) {
      const lat =
        found?.status?.coords?.lat ??
        (found?.status?.location?.split(',')[0]
          ? parseFloat(found.status.location.split(',')[0])
          : null);
      const lng =
        found?.status?.coords?.lon ??
        (found?.status?.location?.split(',')[1]
          ? parseFloat(found.status.location.split(',')[1])
          : null);
      if (lat && lng) {
        setInfoWindowDetail({ lat, lng, tracker: found });
        setShowInfoWindow(true);
      }
    }
  }, [trackers, route?.params?.tag]);

  const handleSelect = (tracker) => {
    setSelectedTagId(tracker?._id || null);
    setSelectedTracker(tracker || null);
    const lat = tracker?.status?.coords?.lat ?? (tracker?.status?.location?.split(',')[0] ? parseFloat(tracker.status.location.split(',')[0]) : null);
    const lng = tracker?.status?.coords?.lon ?? (tracker?.status?.location?.split(',')[1] ? parseFloat(tracker.status.location.split(',')[1]) : null);
    if (lat && lng) {
      setInfoWindowDetail({ lat, lng, tracker });
      setShowInfoWindow(true);
    }
  };

  useEffect(() => {
    if (!selectedTagId) {
      setReservations([]);
      return;
    }

    let isActive = true;
    setReservationsLoading(true);
    getReservationsByTrackingId(selectedTagId, (jsonRes, status) => {
      if (!isActive) return;
      switch (status) {
        case 200:
          setReservations(jsonRes || []);
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
      setReservationsLoading(false);
    });

    return () => {
      isActive = false;
    };
  }, [selectedTagId]);

  const parseDate = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) return null;
    return parsed;
  };

  const formatDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    return date.toLocaleDateString();
  };

  const normalizeDates = (res) => {
    const start = res.startDate || res.start_date || res.start || res.from;
    const end = res.endDate || res.end_date || res.end || res.to;
    return {
      startDate: parseDate(start),
      endDate: parseDate(end),
    };
  };

  const filteredReservations = useMemo(() => {
    const now = new Date();
    const active: any[] = [];
    const future: any[] = [];

    (reservations || []).forEach((res) => {
      const { startDate, endDate } = normalizeDates(res);

      if (endDate && endDate < now) {
        return;
      }

      if (startDate && startDate > now) {
        future.push(res);
        return;
      }

      // Treat missing dates or current window as active
      active.push(res);
    });

    return { active, future };
  }, [reservations]);

  const currentActiveReservation = useMemo(() => {
    if (!filteredReservations.active.length) return null;
    const now = new Date();
    const scored = filteredReservations.active.map((res) => {
      const { startDate, endDate } = normalizeDates(res);
      const ongoing = (!startDate || startDate <= now) && (!endDate || endDate >= now);
      return { res, endDate, ongoing };
    });

    const ongoing = scored
      .filter((s) => s.ongoing)
      .sort((a, b) => {
        if (!a.endDate && b.endDate) return -1;
        if (a.endDate && !b.endDate) return 1;
        if (a.endDate && b.endDate) return a.endDate.getTime() - b.endDate.getTime();
        return 0;
      });

    if (ongoing.length) return ongoing[0].res;
    return scored[0].res;
  }, [filteredReservations]);

  const activeReservationGlyph = useMemo(() => {
    if (!currentActiveReservation) return undefined;
    const label =
      currentActiveReservation.display_names ||
      currentActiveReservation.families ||
      currentActiveReservation.order_number ||
      currentActiveReservation.code ||
      currentActiveReservation.title ||
      '';
    if (!label) return undefined;
    return label.toString().charAt(0).toUpperCase();
  }, [currentActiveReservation]);

  const buildDateRange = (res) => {
    const start = formatDate(res.startDate || res.start_date || res.start || res.from);
    const end = formatDate(res.endDate || res.end_date || res.end || res.to);
    if (start && end) return `${start} - ${end}`;
    return res.dates || res.dateRange || null;
  };

  const stageBadgeColors = (stage?: any) => {
    switch (stage) {
      case 1:
      case '1':
        return { bg: '#262E32', fg: '#ffffff' };
      case 2:
      case '2':
        return { bg: '#4379FF', fg: '#ffffff' };
      case 3:
      case '3':
        return { bg: '#B8393C', fg: '#ffffff' };
      case 4:
      case '4':
        return { bg: '#4CBA70', fg: '#ffffff' };
      case 5:
      case '5':
        return { bg: '#666', fg: '#ffffff' };
      default:
        return null;
    }
  };

  const badgeStyle = (statusLabel?: string) => {
    const normalized = (statusLabel || '').toLowerCase();
    if (normalized.includes('confirm')) return { bg: '#c8f0d6', fg: '#0f7a2a' };
    if (normalized.includes('pend')) return { bg: '#ffe7c2', fg: '#b45309' };
    if (normalized.includes('cancel') || normalized.includes('fail')) return { bg: '#fbd0d0', fg: '#b91c1c' };
    return { bg: '#cdeafe', fg: '#0b8ac9' };
  };

  const renderReservations = (list, emptyLabel) => {
    if (reservationsLoading) {
      return (
        <View style={[styles.emptyState, { flexDirection: 'row', gap: 8 }]}>
          <ActivityIndicator size="small" color="#0ea5e9" />
          <Text style={styles.emptyStateText}>Loading reservations...</Text>
        </View>
      );
    }

    if (!list.length) {
      return (
        <View style={styles.emptyState}>
          <Text style={styles.emptyStateText}>{emptyLabel}</Text>
        </View>
      );
    }

    return (
      <View style={styles.reservationsContainer}>
        {list.map((res, idx) => {
          const productName = res.display_names || res.families;
          const title = res.order_number || res.code || res.title || 'Reservation';
          const subtitle = res.brand || res.full_name || res.subtitle || res.name || res.model || res.type;
          const dateRange = buildDateRange(res);
          const email = res.email || res.customerEmail || res.userEmail || res.customer_email || res.user?.email;
          const statusLabel = res.status_text || res.status || res.state;
          const stageColor = stageBadgeColors(res.stage);
          const { bg, fg } = stageColor || badgeStyle(statusLabel);
          return (
            <View key={idx} style={styles.reservationCard}>
              <View style={styles.reservationRow}>
                <TouchableOpacity>
                  <Text style={[styles.reservationTitle, { color: '#2563eb' }]}>{title}</Text>
                </TouchableOpacity>
                {!!statusLabel && <Text style={[styles.reservationBadge, { backgroundColor: bg, color: fg }]}>{statusLabel}</Text>}
              </View>
              {!!subtitle && <Text style={styles.reservationSubtitle}>{subtitle}</Text>}
              {!!productName && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4 }}>
                  <FontAwesome5 name="cube" size={14} color="#6b7280" />
                  <Text style={styles.reservationMeta}>{productName}</Text>
                </View>
              )}
              {!!dateRange && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 6 }}>
                  <FontAwesome5 name="calendar-alt" size={14} color="#6b7280" />
                  <Text style={styles.reservationMeta}>{dateRange}</Text>
                </View>
              )}
              {!!email && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4 }}>
                  <FontAwesome5 name="user" size={14} color="#6b7280" />
                  <Text style={styles.reservationMeta}>{email}</Text>
                </View>
              )}
            </View>
          );
        })}
      </View>
    );
  };

  const renderDetails = useMemo(() => {
    if (!selectedTracker) {
      return (
        <View style={styles.emptyState}>
          <Text style={styles.emptyStateText}>Select a tracker to view details.</Text>
        </View>
      );
    }

    const name = selectedTracker?.prefs?.name || selectedTracker?.name || selectedTracker?._id || 'Unnamed';
    const idLabel = selectedTracker?._id ? `ID: ${selectedTracker._id}` : '';
    const status = selectedTracker?.status;
    const lastSeen = status?.status_last_updated
      ? new Date(status.status_last_updated).toLocaleString()
      : null;
    const speed = status?.speed;
    const signal = status?.signal;
    const battery = status?.charge;
    const location = status?.location || '';
    const online = status?.status === 1 || status?.status === 'online';

    return (
      <View style={styles.detailCard}>
        <View style={styles.trackerHeader}>
          <View style={styles.trackerAvatar}>
            <Text style={styles.trackerAvatarText}>{(name || 'T').charAt(0).toUpperCase()}</Text>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.detailTitle}>{name}</Text>
            {!!idLabel && <Text style={styles.detailSubtitle}>{idLabel}</Text>}
          </View>
          <View style={styles.statusPill}>
            <View style={[styles.statusDot, { backgroundColor: online ? '#22c55e' : '#9ca3af' }]} />
            <Text style={styles.statusPillText}>{online ? 'Online' : 'Offline'}</Text>
          </View>
        </View>

        <View style={styles.statGrid}>
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>Speed</Text>
            <Text style={styles.statValue}>{speed != null ? `${speed} km/h` : '—'}</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>Signal</Text>
            <Text style={styles.statValue}>{signal != null ? `${signal}%` : '—'}</Text>
          </View>
        </View>

        <View style={styles.statGrid}>
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>Power</Text>
            <Text style={styles.statValue}>{battery ? 'Power Connected' : 'On Battery'}</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>Last Seen</Text>
            <Text style={styles.statValue}>{lastSeen || '—'}</Text>
          </View>
        </View>

        {!!location && (
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Location:</Text>
            <Text style={styles.detailValue}>{location}</Text>
          </View>
        )}
      </View>
    );
  }, [selectedTracker]);

  const mapCenter = useMemo(() => {
    const rawLat = selectedTracker?.status?.coords?.lat;
    const rawLng = selectedTracker?.status?.coords?.lon;
    const loc = selectedTracker?.status?.location;

    const parsedLat =
      rawLat ??
      (loc && loc.split(',')[0] ? parseFloat(loc.split(',')[0]) : undefined);
    const parsedLng =
      rawLng ??
      (loc && loc.split(',')[1] ? parseFloat(loc.split(',')[1]) : undefined);

    if (parsedLat && parsedLng) return { lat: parsedLat, lng: parsedLng };
    return MapPosition;
  }, [selectedTracker]);

  const renderMap = useMemo(() => {
    if (!trackingTagsEnabled) {
      return (
        <View style={styles.emptyState}>
          <Text style={styles.emptyStateText}>Tracking tags are disabled.</Text>
        </View>
      );
    }

    if (!trackers || trackers.length === 0) {
      return (
        <View style={styles.emptyState}>
          <Text style={styles.emptyStateText}>No tracker coordinates available.</Text>
        </View>
      );
    }

    return (
      <View style={styles.mapContainer}>
        <Map
          mapId={GOOGLE_MAP_Id}
          defaultZoom={13.5}
          center={mapCenter}
          gestureHandling="greedy"
          disableDefaultUI={true}
          onClick={() => {
            setShowInfoWindow(false);
            setInfoWindowDetail(null);
          }}
        >
          {trackers.map((tracker) => {
            const lat =
              tracker?.status?.coords?.lat ??
              (tracker?.status?.location?.split(',')[0]
                ? parseFloat(tracker.status.location.split(',')[0])
                : null);
            const lng =
              tracker?.status?.coords?.lon ??
              (tracker?.status?.location?.split(',')[1]
                ? parseFloat(tracker.status.location.split(',')[1])
                : null);
            if (!lat || !lng) return null;
            return (
              <AdvancedMarker
                key={tracker?._id}
                position={{ lat, lng }}
                onClick={() => handleSelect(tracker)}
              >
                <Pin
                  background={selectedTagId === tracker?._id ? '#38bdf8' : '#8B4513'}
                  borderColor={selectedTagId === tracker?._id ? '#38bdf8' : '#8B4513'}
                  glyphColor="white"
                />
              </AdvancedMarker>
            );
          })}
          {showInfoWindow && infoWindowDetail?.lat && infoWindowDetail?.lng && (
            <InfoWindow
              position={{ lat: infoWindowDetail.lat, lng: infoWindowDetail.lng }}
              pixelOffset={[0, -30]}
              headerDisabled
              onCloseClick={() => {
                setShowInfoWindow(false);
                setInfoWindowDetail(null);
              }}
            >
              <div style={{ minWidth: 280 }}>
                <InfoWindowCard
                  details={{
                    id: infoWindowDetail.tracker?._id,
                    name:
                      infoWindowDetail.tracker?.prefs?.name ||
                      infoWindowDetail.tracker?.name ||
                      infoWindowDetail.tracker?._id ||
                      'Tracker',
                    status: infoWindowDetail.tracker?.status,
                    owner: infoWindowDetail.tracker?.owner,
                    subscription: infoWindowDetail.tracker?.subscription,
                    prefs: infoWindowDetail.tracker?.prefs,
                    currentReservation: currentActiveReservation,
                  }}
                  onClose={() => {
                    setShowInfoWindow(false);
                    setInfoWindowDetail(null);
                  }}
                />
              </div>
            </InfoWindow>
          )}
        </Map>
      </View>
    );
  }, [trackers, mapCenter, selectedTagId, showInfoWindow, infoWindowDetail]);

  return (
    !trackingTagsEnabled ? (
      <BasicLayout navigation={navigation} screenName={'Tracking Tags'}>
        <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
          <Text style={styles.emptyStateText}>Tracking tags are disabled.</Text>
        </View>
      </BasicLayout>
    ) : (
    <BasicLayout
      navigation={navigation}
      screenName={'Tracking Tags'}
    >
      <View style={styles.container}>
        <View style={styles.dashboardWrapper}>
          <View style={styles.leftPane}>
            <View style={styles.sectionHeader}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <View
                    style={{
                      marginRight: 10,
                      borderWidth: 2,
                      borderColor: '#2e96e1',
                      borderRadius: 999,
                      padding: 6,
                    }}
                  >
                    <FontAwesome5 name="crosshairs" size={16} color="#2e96e1" />
                  </View>
                  <Text style={styles.sectionTitle}>
                    {selectedTracker?.prefs?.name || selectedTracker?.name || selectedTracker?._id || 'Tracker'}
                  </Text>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 12 }}>
                  <FontAwesome5
                    name="link"
                    size={14}
                    color={selectedTracker?.status?.status === 1 || selectedTracker?.status === 'online' ? '#22c55e' : '#9ca3af'}
                    style={{ marginRight: 6 }}
                  />
                  <Text
                    style={{
                      color:
                        selectedTracker?.status?.status === 1 || selectedTracker?.status === 'online'
                          ? '#22c55e'
                          : '#6b7280',
                      fontWeight: '600',
                    }}
                  >
                    {(selectedTracker?.status?.status === 1 || selectedTracker?.status === 'online') ? 'Online' : 'Offline'}
                  </Text>
                  <View
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 999,
                      backgroundColor:
                        selectedTracker?.status?.status === 1 || selectedTracker?.status === 'online'
                          ? '#22c55e'
                          : '#9ca3af',
                      marginLeft: 8,
                    }}
                  />
                </View>
              </View>
            </View>
            <View style={{ height: 1, backgroundColor: '#e5e7eb', marginVertical: 8 }} />

            {selectedTracker && (
              <View style={{ gap: 8 }}>
                <View style={styles.statGrid}>
                  <View style={[styles.statCard, styles.headerStatCard]}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <FontAwesome5 name="tachometer-alt" size={16} color="#0ea5e9" />
                      <Text style={styles.statValue}>{headerStats.speed != null ? `${headerStats.speed} km/h` : '—'}</Text>
                    </View>
                  </View>
                  <View style={[styles.statCard, styles.headerStatCard]}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <FontAwesome5 name="signal" size={16} color="#0ea5e9" />
                      <Text style={styles.statValue}>{headerStats.signal != null ? `${headerStats.signal}%` : '—'}</Text>
                    </View>
                  </View>
                </View>
                <View style={styles.statGrid}>
                  <View style={[styles.statCard, styles.headerStatCard]}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <FontAwesome5 name="map-marker-alt" size={16} color="#0ea5e9" />
                      <Text style={styles.statValue}>{headerStats.lastSeen || '—'}</Text>
                    </View>
                  </View>
                  <View style={[styles.statCard, styles.headerStatCard]}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <FontAwesome5
                        name="bolt"
                        size={16}
                        color="#0ea5e9"
                        style={{ width: 18, textAlign: 'center' }}
                      />
                      <Text style={styles.statValue}>{headerStats.powerConnected}</Text>
                    </View>
                  </View>
                </View>
              </View>
            )}
            <View style={{ height: 1, backgroundColor: '#e5e7eb', marginVertical: 8 }} />
            {/* <View style={styles.tableContainer}>
              <View style={styles.tableHeader}>
                <Text style={styles.columnHeader}>{'Trackers'}</Text>
              </View>
              <ScrollView style={{ flex: 1, maxHeight: screenHeight - 260 }}>
                {renderTrackerList()}
              </ScrollView>
            </View> */}

            <ScrollView
              style={{ maxHeight: screenHeight - 220 }}
              contentContainerStyle={{ paddingBottom: 16, gap: 16 }}
              showsVerticalScrollIndicator={true}
            >
              <View style={styles.sectionHeader}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                  <Text style={styles.sectionTitle}>Active Reservations</Text>
                  <View style={{ backgroundColor: '#e0f2fe', borderRadius: 999, paddingHorizontal: 8, paddingVertical: 2 }}>
                    <Text style={{ color: '#0ea5e9', fontWeight: '700', fontSize: 14 }}>
                      {filteredReservations.active.length}
                    </Text>
                  </View>
                </View>
              </View>
              {renderReservations(filteredReservations.active, 'No active reservations.')}

              <View style={styles.sectionHeader}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                  <Text style={styles.sectionTitle}>Future Reservations</Text>
                  <View style={{ backgroundColor: '#e0f2fe', borderRadius: 999, paddingHorizontal: 8, paddingVertical: 2 }}>
                    <Text style={{ color: '#0ea5e9', fontWeight: '700', fontSize: 14 }}>
                      {filteredReservations.future.length}
                    </Text>
                  </View>
                </View>
              </View>
              {renderReservations(filteredReservations.future, 'No future reservations.')}
            </ScrollView>
          </View>

          <View style={styles.rightPane}>{renderMap}</View>
        </View>
      </View>
    </BasicLayout>
    )
  );
};

const styles = trackingTagsStyle;

export default TrackingTags;
